import React, { FC, useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
	MOBILE_DASHBOARD_MOBILE_FLOW_ANALYZER_CONTENT_DAID,
	MOBILE_DASHBOARD_MOBILE_SDKS_CONTENT_DAID
} from 'data-automation-ids/MobileDashboardAutomationIds';
import { WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID } from 'data-automation-ids/WfaDashboardAutomationIds';
import { BusinessUnitLoadingStates } from 'src/apps/dashboard/components/business-unit-loading-states/BusinessUnitLoadingStates';
import { BusinessUnitMobileRunsTable } from 'src/apps/dashboard/components/business-unit-mobile-runs-table/BusinessUnitMobileRunsTable';
import {
	BUSINESS_UNIT_MFA_TABLE_ID,
	BUSINESS_UNIT_MOBILE_SDK_TABLE_ID
} from 'src/apps/dashboard/constants/Dashboard';
import { MOBILE_FILTER_DEFINITIONS } from 'src/apps/dashboard/constants/MobileDashboardDefaults';
import { useBusinessUnitLabelDisplayName } from 'src/apps/dashboard/helpers/BusinessUnitLabelHelper';
import { buildLabelsExcludedFromDashboard } from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { useEffectiveFilters } from 'src/apps/dashboard/helpers/FilterHelpers';
import { useBusinessUnitRedirect } from 'src/apps/dashboard/hooks/useBusinessUnitRedirect';
import { useMfaSessionsQuery } from 'src/apps/dashboard/hooks/useMfaSessionsQuery';
import { useMobileAppIdFilterOptionsQuery } from 'src/apps/dashboard/hooks/useMobileAppIdFilterOptionsQuery';
import { useMobileDashboardDataQuery } from 'src/apps/dashboard/hooks/useMobileDashboardDataQuery';
import { useMobileScoreDataQuery } from 'src/apps/dashboard/hooks/useMobileScoreDataQuery';
import { useMobileSdkTestsQuery } from 'src/apps/dashboard/hooks/useMobileSdkTestsQuery';
import { useMobileBusinessUnitsFromConfig } from 'src/apps/dashboard/stores/dashboardConfigStore';
import { DashboardTemplate } from 'src/apps/dashboard/templates/DashboardTemplate';
import { DashboardRouteParams } from 'src/apps/dashboard/types/DashboardTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { usePlatformConfigInitializer } from 'src/common/hooks/usePlatformConfigInitializer';
import { DASHBOARD_LABELS } from 'src/common/labels/dashboard.labels';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';
import { useDashboardFiltersStore } from 'src/common/stores/dashboardFiltersStore';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import { HEADER, MOBILE_RESET_TABLE_IDS } from './MobileDashboardBusinessUnits.constants';

export const MobileDashboardBusinessUnits: FC = () => {
	usePlatformConfigInitializer();
	const { unitId } = useParams<DashboardRouteParams>();
	const history = useHistory();
	const { filters, setFilters } = useDashboardFiltersStore();
	const { businessUnits, isLoading: isConfigLoading } = useMobileBusinessUnitsFromConfig();
	const { isUniquenessServiceEnabled, mobileCoreLabels } = usePlatformConfigStore();
	const businessUnitDisplayName = useBusinessUnitLabelDisplayName();

	const labelsExcludedFromDashboard = useMemo(
		() => buildLabelsExcludedFromDashboard(mobileCoreLabels),
		[mobileCoreLabels]
	);

	const effectiveFilters = useEffectiveFilters(filters, MOBILE_FILTER_DEFINITIONS);
	const { onTablePageOrPageSizeChange, getTableStateById } = useTableState();

	const [activeTabIndex, setActiveTabIndex] = useState(0);

	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions) => {
			const currentDateRange = filters?.dateRange?.[0]?.value;
			const newDateRange = newFilters?.dateRange?.[0]?.value;

			if (currentDateRange !== newDateRange) {
				MOBILE_RESET_TABLE_IDS.forEach((tableId) => {
					const state = getTableStateById(tableId);
					onTablePageOrPageSizeChange(tableId, {
						...state.paginationInfo,
						page: 1
					});
				});
			}

			setFilters(newFilters);
		},
		[filters, setFilters, getTableStateById, onTablePageOrPageSizeChange]
	);

	const businessUnitName =
		businessUnits.find((bu) => bu.id === unitId)?.name ?? unitId ?? 'Unknown';

	useBusinessUnitRedirect({ unitId, businessUnits, history });

	const filtersWithBusinessUnit = useMemo(() => {
		if (!unitId) {
			return effectiveFilters;
		}
		return {
			...effectiveFilters,
			unitId: [{ value: unitId, label: businessUnitName }]
		};
	}, [effectiveFilters, unitId, businessUnitName]);

	const dashboardDataQuery = useMobileDashboardDataQuery(
		filtersWithBusinessUnit,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);
	const scoreDataQuery = useMobileScoreDataQuery(
		filtersWithBusinessUnit,
		MOBILE_FILTER_DEFINITIONS,
		labelsExcludedFromDashboard
	);

	const filterOptions = useMobileAppIdFilterOptionsQuery();

	const filter = useMemo(
		() => ({
			filters,
			filterDefinitions: MOBILE_FILTER_DEFINITIONS,
			filterOptions,
			onFilterChange: handleFilterChange
		}),
		[filters, filterOptions, handleFilterChange]
	);

	const scoreQuery = useMemo(
		() => ({
			data: scoreDataQuery.data,
			isLoading: scoreDataQuery.isLoading,
			error: scoreDataQuery.error
		}),
		[scoreDataQuery.data, scoreDataQuery.isLoading, scoreDataQuery.error]
	);

	const dashboardQuery = useMemo(
		() => ({
			data: dashboardDataQuery.data,
			isLoading: dashboardDataQuery.isLoading,
			error: dashboardDataQuery.error
		}),
		[dashboardDataQuery.data, dashboardDataQuery.isLoading, dashboardDataQuery.error]
	);

	const { tabs, tabPanels } = useMemo(() => {
		const subTabs = [
			{
				id: 'mobile-flow-analyzer',
				title: DASHBOARD_LABELS.MOBILE_FLOW_ANALYZER_TAB,
				content: (
					<div
						className="mobile-flow-analyzer-content"
						data-automation-id={MOBILE_DASHBOARD_MOBILE_FLOW_ANALYZER_CONTENT_DAID}
						role="region"
						aria-label={DASHBOARD_LABELS.MOBILE_FLOW_ANALYZER_TAB}
					>
						<BusinessUnitMobileRunsTable
							dashboardFilters={filtersWithBusinessUnit}
							unitIdLabel={businessUnitName}
							tableId={BUSINESS_UNIT_MFA_TABLE_ID}
							nameLabel={DASHBOARD_LABELS.MFA_SESSION_NAME_LABEL}
							useRunsQuery={useMfaSessionsQuery}
						/>
					</div>
				)
			},
			{
				id: 'mobile-sdks',
				title: DASHBOARD_LABELS.MOBILE_SDKS_TAB,
				content: (
					<div
						className="mobile-sdks-content"
						data-automation-id={MOBILE_DASHBOARD_MOBILE_SDKS_CONTENT_DAID}
						role="region"
						aria-label={DASHBOARD_LABELS.MOBILE_SDKS_TAB}
					>
						<BusinessUnitMobileRunsTable
							dashboardFilters={filtersWithBusinessUnit}
							unitIdLabel={businessUnitName}
							tableId={BUSINESS_UNIT_MOBILE_SDK_TABLE_ID}
							nameLabel={DASHBOARD_LABELS.MOBILE_SDK_TEST_NAME_LABEL}
							useRunsQuery={useMobileSdkTestsQuery}
						/>
					</div>
				)
			}
		];

		const tabs = subTabs.map((subTab, index) => ({
			id: `tab-${subTab.id}`,
			tablistId: 'dashboard-bottom-tabs',
			ref: null,
			isActive: index === activeTabIndex,
			// eslint-disable-next-line @typescript-eslint/no-empty-function
			handleKeyDown: () => {},
			children: subTab.title,
			ariaControls: `panel-${subTab.id}`,
			ariaLabel: subTab.title,
			onActivate: () => setActiveTabIndex(index),
			dataAutomationId: `tab-${subTab.id}`
		}));

		const tabPanels = subTabs.map((subTab, index) => ({
			id: `panel-${subTab.id}`,
			ariaLabelledBy: `panel-label-${subTab.id}`,
			isActive: index === activeTabIndex,
			children: (
				<div className="sub-tab-content">
					<span id={`panel-label-${subTab.id}`} className="sr-only">
						{subTab.title}
					</span>
					{subTab.content}
				</div>
			)
		}));

		return { tabs, tabPanels };
	}, [activeTabIndex, businessUnitName, filtersWithBusinessUnit]);

	const bottom = useMemo(
		() => ({
			kind: 'tabs' as const,
			sectionVariant: 'businessUnitSubTabs' as const,
			title: `Tests labeled with this ${businessUnitDisplayName || 'business unit'}`,
			tabState: {
				tabs,
				tabPanels,
				activeTabIndex,
				onTabSelected: setActiveTabIndex,
				tabsId: 'business-unit-tabs',
				tabsAriaLabel: 'Business unit sub-tabs',
				tabsDataAutomationId: 'business-unit-tabs'
			}
		}),
		[businessUnitDisplayName, tabs, tabPanels, activeTabIndex]
	);

	if (isConfigLoading || (!isConfigLoading && businessUnits.length === 0) || !unitId) {
		return (
			<BusinessUnitLoadingStates
				isConfigLoading={isConfigLoading}
				businessUnits={businessUnits}
				unitId={unitId}
			/>
		);
	}

	return (
		<DashboardTemplate
			containerAutomationId={WFA_DASHBOARD_BUSINESS_UNITS_CONTAINER_DAID}
			header={HEADER}
			filter={filter}
			effectiveFilters={filtersWithBusinessUnit}
			scoreQuery={scoreQuery}
			dashboardQuery={dashboardQuery}
			isUniquenessServiceEnabled={isUniquenessServiceEnabled}
			bottom={bottom}
		/>
	);
};
